// /nz-green-loan-guide — Helps homeowners prepare for eligible NZ green
// home-loan top-ups. AeroMax is not a lender; we help prepare a quote pack.
import { Link } from "react-router-dom";
import {
  Banknote,
  FileText,
  Cpu,
  TrendingUp,
  Wrench,
  ArrowRight,
  ExternalLink,
  ShieldAlert,
  Sparkles,
  Info,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";

const HELP_CARDS = [
  {
    icon: FileText,
    title: "Quote-ready pack",
    body:
      "We provide an AeroMax quote showing the recommended hot water heat pump, installed cost range, and scope of work.",
  },
  {
    icon: Cpu,
    title: "Product information",
    body:
      "We provide product / model details for the NeoPower Black Shield hot water heat pump where required.",
  },
  {
    icon: TrendingUp,
    title: "Savings estimate",
    body:
      "We provide the customer's savings calculator summary and the key assumptions used.",
  },
  {
    icon: Wrench,
    title: "Installer & installation details",
    body:
      "Where available, we provide installation timing, assessment notes and installer details once confirmed.",
  },
];

const BANKS = [
  {
    bank: "ANZ",
    name: "Good Energy Home Loan",
    useFor:
      "Eligible energy efficiency upgrades including heat pump water heaters and water heating systems.",
    cta: "Apply / Learn more with ANZ",
    href:
      "https://www.anz.co.nz/personal/home-loans-mortgages/loan-types/good-energy/",
  },
  {
    bank: "ASB",
    name: "Better Homes Top Up",
    useFor:
      "Home upgrades that may make the home warmer, drier or more energy efficient. Electric water heat pumps are listed as an efficient hot-water option.",
    cta: "Apply / Learn more with ASB",
    href:
      "https://www.asb.co.nz/home-loans-mortgages/better-homes-top-up.html",
  },
  {
    bank: "BNZ",
    name: "Better Future Home Loan Top Up",
    useFor:
      "Eligible upgrades such as home insulation, ventilation and heating. Customers should confirm hot water heat pump eligibility directly with BNZ before applying.",
    cta: "Apply / Learn more with BNZ",
    href:
      "https://www.bnz.co.nz/personal-banking/home-loans/manage-your-loan/top-ups/better-future-home-loan-top-ups",
  },
  {
    bank: "Westpac",
    name: "Greater Choices Home Loan",
    useFor:
      "Eligible energy efficiency / climate resilience upgrades. Hot water heat pumps may be eligible — customers will need to provide quote and supporting details to Westpac.",
    cta: "Apply / Learn more with Westpac",
    href:
      "https://www.westpac.co.nz/home-loans-mortgages/options/greater-choices-home-loan/",
  },
  {
    bank: "Kiwibank",
    name: "Sustainable Energy Loan",
    useFor:
      "Eligible sustainable energy systems.",
    note:
      "Based on Kiwibank's published criteria, this loan may require eligible systems to have at least a 10-year manufacturer's warranty and meet specific sustainable energy system requirements. NeoPower Black Shield currently has a 7-year warranty, so customers should confirm eligibility directly with Kiwibank before relying on this option.",
    cta: "Check eligibility with Kiwibank",
    href:
      "https://www.kiwibank.co.nz/personal-banking/home-loans/getting-a-home-loan/sustainable-energy-loan/",
    flagged: true,
  },
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Banknote className="h-3 w-3 text-[#10B981]" /> NZ Green Loan Guide
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Make your hot water upgrade <span className="text-[#10B981]">easier to finance</span>.
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
            AeroMax helps homeowners prepare the documents they may need when applying for eligible NZ green home loan top-ups.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="py-14 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            <Info className="h-3 w-3" /> What this is
          </span>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
            A pack, not a loan.
          </h2>
        </div>
        <div className="lg:col-span-2 space-y-4 text-[#475569] leading-relaxed">
          <p>
            Many NZ banks offer <strong className="text-[#0B132B]">low-interest or special home-loan top-up options</strong> for eligible energy efficiency upgrades.
          </p>
          <p>
            AeroMax does <strong className="text-[#0B132B]">not provide financial advice and does not arrange lending</strong>.
          </p>
          <p>
            What we do is help customers prepare a <strong className="text-[#0B132B]">"Loan Apply Ready Pack"</strong> for their bank when installing a hot water heat pump through AeroMax Energy.
          </p>
        </div>
      </div>
    </section>
  );
}

function HowWeHelp() {
  return (
    <section className="py-14 sm:py-20 bg-[#F8FAFC] border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            <Sparkles className="h-3 w-3" /> How AeroMax helps
          </span>
          <h2 className="font-heading mt-3 text-2xl sm:text-4xl font-bold text-[#0B132B] leading-tight">
            Everything your bank may ask for, in one tidy pack.
          </h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 sm:gap-6">
          {HELP_CARDS.map((c, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 hover:shadow-xl hover:shadow-emerald-500/8 hover:border-[#10B981]/30 transition-all">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
                  <c.icon className="h-5 w-5" />
                </span>
                <h3 className="font-heading mt-4 text-lg sm:text-xl font-bold text-[#0B132B]">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm sm:text-[15px] text-[#475569] leading-relaxed">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BankCards() {
  return (
    <section className="py-14 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            <Banknote className="h-3 w-3" /> Available options
          </span>
          <h2 className="font-heading mt-3 text-2xl sm:text-4xl font-bold text-[#0B132B] leading-tight">
            NZ green home loan options
          </h2>
          <p className="mt-3 text-[#475569] leading-relaxed">
            These are public bank offerings that may support eligible energy efficiency upgrades. Final eligibility, rates and terms are set by each bank.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 sm:gap-6">
          {BANKS.map((b, i) => (
            <Reveal key={b.bank} delay={i * 50}>
              <article
                data-testid={`bank-card-${b.bank.toLowerCase()}`}
                className="h-full flex flex-col rounded-2xl border border-[#E2E8F0] bg-white p-7 sm:p-8 hover:shadow-xl hover:shadow-emerald-500/8 hover:border-[#10B981]/30 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B132B] text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                      {b.bank}
                    </span>
                    <h3 className="font-heading mt-3 text-xl sm:text-2xl font-bold text-[#0B132B] leading-snug">
                      {b.name}
                    </h3>
                  </div>
                  {b.flagged && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold tracking-[0.14em] uppercase shrink-0">
                      <ShieldAlert className="h-3 w-3" /> Check eligibility
                    </span>
                  )}
                </div>
                <p className="mt-4 text-sm sm:text-[15px] text-[#475569] leading-relaxed">
                  <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#0B132B] block mb-1">
                    Use for
                  </span>
                  {b.useFor}
                </p>
                {b.note && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-900 leading-relaxed">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="h-4 w-4 mt-[2px] shrink-0" />
                      <p>{b.note}</p>
                    </div>
                  </div>
                )}
                <div className="mt-auto pt-6">
                  <a
                    href={b.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`bank-cta-${b.bank.toLowerCase()}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-5 py-2.5 text-sm font-semibold transition-all"
                  >
                    {b.cta}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Disclaimer() {
  return (
    <section className="py-12 bg-[#F8FAFC] border-y border-[#E2E8F0]">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700 shrink-0">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#0B132B]">
                Disclaimer
              </p>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                AeroMax Energy is <strong className="text-[#0B132B]">not a lender, mortgage adviser or financial adviser</strong>. Finance approval, eligibility, interest rates, terms and conditions are determined by each bank. Customers should speak directly with their bank or financial adviser before applying. AeroMax can help prepare product, quote and savings information for the customer's application.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-gradient-to-br from-[#0B132B] via-[#0B132B] to-[#0F1F45] p-8 sm:p-14 text-white">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#10B981]/15 blur-3xl pointer-events-none" />
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur text-[10px] font-bold tracking-[0.22em] uppercase text-white/85">
            <Sparkles className="h-3 w-3 text-[#10B981]" /> Ready to apply?
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold leading-tight max-w-2xl">
            Ready to prepare your loan pack?
          </h2>
          <p className="mt-3 text-white/75 max-w-2xl leading-relaxed">
            Complete a free home assessment and we'll help prepare the key upgrade information your bank may ask for.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/free-home-assessment"
              data-testid="green-loan-cta-assessment"
              className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30"
            >
              Book Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/savings-calculator"
              data-testid="green-loan-cta-calc"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur text-white px-6 py-3.5 font-semibold transition-all"
            >
              Run Savings Calculator
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function GreenLoanGuide() {
  return (
    <div data-testid="green-loan-guide-page" className="bg-white">
      <Hero />
      <Intro />
      <HowWeHelp />
      <BankCards />
      <Disclaimer />
      <CTA />
    </div>
  );
}
